import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  Button,
  TextField,
  Chip,
  InputAdornment,
  Avatar,
  Divider,
  Stack,
} from '@mui/material';
import {
  Search,
  Add,
  Visibility,
  Logout,
  AdminPanelSettings,
  People,
  Payment,
  Phone,
  CreditCard,
  CalendarToday,
  Business,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { UserDTO, UserStatus } from '../types';
import adminService from '../services/adminService';
import { organizationService } from '../services/organizationService'; // Импортируем organizationService как именованный экспорт
import CreatePaymentDialog from '../components/CreatePaymentDialog';
import UserDetailsDialog from '../components/UserDetailsDialog';
import { formatPhone } from '../utils/phoneFormatter';

const AdminDashboardPage: React.FC = () => {
  const { state, logout } = useAuth();
  const { user } = state;
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserDTO[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  // const [isLoading, setIsLoading] = useState(true);
  const [isCreatePaymentOpen, setIsCreatePaymentOpen] = useState(false);
  const [isUserDetailsOpen, setIsUserDetailsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserDTO | null>(null);
  const [organizationsCount, setOrganizationsCount] = useState<number | null>(null); // Новое состояние для количества организаций

  useEffect(() => {
    loadUsers();
    loadOrganizationsCount(); // Загружаем количество организаций при монтировании
  }, []);

  const filterUsers = useCallback(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }
    const lowerCaseQuery = searchQuery.toLowerCase();
    const filtered = users.filter(user =>
      user.fullName?.toLowerCase().includes(lowerCaseQuery) ||
      user.phone.toString().includes(lowerCaseQuery) || // Используем user.phone
      user.city?.name.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredUsers(filtered);
  }, [users, searchQuery]);

  useEffect(() => {
    filterUsers();
  }, [users, searchQuery, filterUsers]); // Добавляем filterUsers в зависимости

  const loadUsers = async () => {
    try {
      const usersData = await adminService.getAllUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
    } finally {
      // setIsLoading(false);
    }
  };

  const loadOrganizationsCount = async () => {
    try {
      const allOrganizations = await organizationService.getAllPlatformOrganizations(); // Используем getAllPlatformOrganizations
      setOrganizationsCount(allOrganizations.length);
    } catch (error) {
      console.error('Ошибка загрузки количества организаций:', error);
      setOrganizationsCount(0);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const handleViewUser = (user: UserDTO) => {
    setSelectedUser(user);
    setIsUserDetailsOpen(true);
  };

  // const handleStatusChange = async (userId: string, isActive: boolean) => {
  //   try {
  //     await adminService.updateUserStatus(userId, isActive);
  //     setUsers(users.map(user =>
  //       user.id === userId ? { ...user, status: isActive ? UserStatus.Selfemployed : UserStatus.Blocked } : user
  //     ));
  //   } catch (error) {
  //     console.error('Ошибка изменения статуса:', error);
  //   }
  // };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  // const formatAmount = (amount: number) => {
  //   return new Intl.NumberFormat('ru-RU', {
  //     style: 'currency',
  //     currency: 'RUB',
  //   }).format(amount);
  // };

  if (!user) return null;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Заголовок */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
            Панель администратора
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
            Управление самозанятыми и выплатами
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Logout />}
          onClick={handleLogout}
          sx={{ 
            px: 3, 
            py: 1.5, 
            borderRadius: 2,
            borderWidth: 2,
            '&:hover': { borderWidth: 2 }
          }}
        >
          Выйти
        </Button>
      </Box>

      <Grid container spacing={4}>
        {/* Статистика */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ 
            p: 4, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            color: 'text.primary',
            borderRadius: 3,
            height: '100%',
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <People sx={{ fontSize: 56, mb: 2, color: 'primary.main' }} />
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
              {users.length}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Всего пользователей
            </Typography>
          </Card>
        </Grid>
        {/* Новая карточка для организаций */}
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0} 
            sx={{
              p: 4, 
              textAlign: 'center',
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              color: 'text.primary',
              borderRadius: 3,
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              cursor: 'pointer',
              '&:hover': { 
                borderColor: 'primary.dark',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }
            }}
            onClick={() => navigate('/admin/organizations')}
          >
            <Business sx={{ fontSize: 56, mb: 2, color: 'secondary.main' }} />
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, color: 'secondary.main' }}>
              {organizationsCount !== null ? organizationsCount : '-'}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Всего организаций
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ 
            p: 4, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            color: 'text.primary',
            borderRadius: 3,
            height: '100%',
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <AdminPanelSettings sx={{ fontSize: 56, mb: 2, color: 'success.main' }} />
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, color: 'success.main' }}>
              {users.filter(u => u.status === 'Selfemployed' && u.cards && u.cards.length > 0).length}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Активных пользователей
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ 
            p: 4, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            color: 'text.primary',
            borderRadius: 3,
            height: '100%',
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Payment sx={{ fontSize: 56, mb: 2, color: 'info.main' }} />
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, color: 'info.main' }}>
              {users.filter(u => u.cards && u.cards.length > 0).length}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              С картами
            </Typography>
          </Card>
        </Grid>

        {/* Управление пользователями */}
        <Grid item xs={12}>
          <Card elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Список самозанятых
              </Typography>
              {users && users.filter(u => u.status === 'Selfemployed' && u.cards && u.cards.length > 0).length > 0 && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setIsCreatePaymentOpen(true)}
                  sx={{ 
                    px: 4, 
                    py: 1.5, 
                    borderRadius: 2
                  }}
                >
                  Создать выплату
                </Button>
              )}
            </Box>

            <TextField
              fullWidth
              placeholder="Поиск по ФИО..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                mb: 4,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                }
              }}
            />

            <Grid container spacing={3}>
              {filteredUsers.map((user) => (
                <Grid item xs={12} md={6} lg={4} key={user.id}>
                  <Card elevation={0} sx={{ 
                    p: 3, 
                    borderRadius: 3, 
                    border: '1px solid', 
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                      borderColor: 'primary.main'
                    }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ 
                        bgcolor: 'primary.main', 
                        mr: 2,
                        width: 48,
                        height: 48
                      }}>
                        {user.personalData?.fullName?.charAt(0) || 'U'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {user.personalData?.fullName || 'Не указано'}
                        </Typography>
                        <Chip
                          label={user.status === 'Selfemployed' ? 'Активен' : 'Не активен'}
                          color={user.status === 'Selfemployed' ? 'success' : 'error'}
                          size="small"
                          sx={{ borderRadius: 1 }}
                        />
                      </Box>
                    </Box>

                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Business sx={{ fontSize: 18, color: 'text.secondary', mr: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          {user.personalData?.inn || 'Не указано'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Phone sx={{ fontSize: 18, color: 'text.secondary', mr: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatPhone(String(user.phone))}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CreditCard sx={{ fontSize: 18, color: 'text.secondary', mr: 1.5 }} />
                        <Chip
                          label={user.cards && user.cards.length > 0 ? 'Есть карта' : 'Нет карты'}
                          color={user.cards && user.cards.length > 0 ? 'success' : 'warning'}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarToday sx={{ fontSize: 18, color: 'text.secondary', mr: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(user.createdAt)}
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => handleViewUser(user)}
                        fullWidth
                        sx={{ 
                          borderRadius: 2,
                          borderWidth: 2,
                          '&:hover': { borderWidth: 2 }
                        }}
                      >
                        Подробнее
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {filteredUsers.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary">
                  Пользователи не найдены
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      <CreatePaymentDialog
        open={isCreatePaymentOpen}
        onClose={() => setIsCreatePaymentOpen(false)}
        users={users}
        onSuccess={() => {
          setIsCreatePaymentOpen(false);
          loadUsers();
        }}
      />

      <UserDetailsDialog
        open={isUserDetailsOpen}
        onClose={() => setIsUserDetailsOpen(false)}
        user={selectedUser}
      />
    </Container>
  );
};

export default AdminDashboardPage;
